import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, UserCheck, Loader2, History, ArrowRight, ChevronDown, ChevronRight as ChevronRightIcon, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDialog } from '@/components/ConfirmDialog';

/**
 * Recent Impersonation Sessions panel — super-admin home dashboard widget.
 *
 * Shows the last N impersonation events (default 5 visible, expand for more).
 * Each row offers:
 *   • A "Resume" button that re-enters the same target user.
 *   • A click-to-expand "Session Replay" timeline showing every audit-log
 *     entry the target user generated in the 2-hour window after the
 *     impersonation started — full forensics trail for compliance.
 *
 * Hidden entirely when the current user isn't a super_admin or when there's
 * no history to show (no clutter on a fresh-DB super-admin view).
 */
export function RecentImpersonationsPanel() {
  const { user, setUserData } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { confirmFn, ConfirmDialogElement } = useConfirmDialog();

  useEffect(() => {
    if (user?.role !== 'super_admin') { setLoading(false); return; }
    api.getImpersonationHistory(25)
      .then(s => setSessions(s || []))
      .catch(err => console.warn('Failed to load impersonation history:', err.message))
      .finally(() => setLoading(false));
  }, [user?.role]);

  const resume = useCallback(async (s) => {
    const ok = await confirmFn({
      title: 'Resume impersonation?',
      message: `Re-enter as ${s.target_name || s.target_email} (tenant ${s.target_tenant_name || s.target_tenant_id}). All actions are audited.`,
      confirmLabel: 'Resume',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await api.impersonateUser(s.target_user_id);
      localStorage.setItem('impersonating', JSON.stringify({ admin_user: user, started_at: new Date().toISOString(), return_to: window.location.pathname + window.location.search }));
      setUserData(res.user);
      toast.success(`Impersonating ${s.target_name || s.target_email}`);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.message || 'Failed to resume impersonation');
    }
  }, [confirmFn, setUserData, user]);

  if (user?.role !== 'super_admin') return null;
  if (loading) {
    return (
      <div className="glass-card rounded-xl p-4 mb-6" data-testid="impersonation-panel-loading">
        <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
      </div>
    );
  }
  if (sessions.length === 0) return null;

  const visible = expanded ? sessions : sessions.slice(0, 5);

  return (
    <div className="glass-card rounded-xl p-4 mb-6" data-testid="recent-impersonations-panel">
      <ConfirmDialogElement />
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2">
          <Shield className="w-4 h-4 text-violet-400" /> Recent Impersonation Sessions
          <span className="text-[10px] font-normal text-muted-foreground/70">({sessions.length})</span>
        </h2>
        <button
          onClick={() => navigate('/tenant-admin')}
          className="text-xs text-primary hover:underline flex items-center gap-1"
          data-testid="impersonation-panel-manage-link"
        >Manage Tenants <ArrowRight className="w-3 h-3" /></button>
      </div>
      <div className="space-y-1">
        {visible.map(s => (
          <ImpersonationRow key={s.audit_id} session={s} onResume={() => resume(s)} />
        ))}
      </div>
      {sessions.length > 5 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-[10px] text-muted-foreground hover:text-foreground mt-2 flex items-center gap-1"
          data-testid="impersonation-panel-toggle-expand"
        >
          <History className="w-3 h-3" /> {expanded ? 'Show less' : `Show all ${sessions.length}`}
        </button>
      )}
    </div>
  );
}

export function ImpersonationRow({ session, onResume }) {
  const adminLabel = session.admin_name || session.admin_email || 'Unknown admin';
  const targetLabel = session.target_name || session.target_email || 'Unknown user';
  const [open, setOpen] = useState(false);

  return (
    <div
      className="rounded-lg border border-border/20 hover:border-border/40 transition-colors group"
      data-testid={`impersonation-row-${session.audit_id}`}
    >
      <div className="flex items-center gap-3 p-2">
        <button
          onClick={() => setOpen(o => !o)}
          className="text-muted-foreground hover:text-foreground flex-shrink-0"
          data-testid={`impersonation-toggle-${session.audit_id}`}
          title={open ? 'Hide session timeline' : 'Show session timeline'}
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}
        </button>
        <div className="w-7 h-7 rounded-full bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-[10px] font-bold text-violet-400 flex-shrink-0">
          {(adminLabel || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-foreground truncate">
            <span className="font-medium">{adminLabel}</span>
            <span className="text-muted-foreground mx-1.5">→</span>
            <span className="font-medium">{targetLabel}</span>
          </p>
          <p className="text-[10px] text-muted-foreground truncate">
            {session.target_tenant_name || session.target_tenant_id}
            <span className="mx-1.5">·</span>
            {formatRelative(session.timestamp)}
            {session.actions_count > 0 && (
              <>
                <span className="mx-1.5">·</span>
                {session.actions_count} action{session.actions_count !== 1 ? 's' : ''}
              </>
            )}
          </p>
        </div>
        {onResume && (
          <button
            onClick={onResume}
            className="text-[10px] px-2 py-1 rounded bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0"
            title="Re-enter this impersonation session"
            data-testid={`impersonation-resume-${session.audit_id}`}
          >
            <UserCheck className="w-2.5 h-2.5" /> Resume
          </button>
        )}
      </div>
      {open && <SessionReplay auditId={session.audit_id} actionsCount={session.actions_count} />}
    </div>
  );
}

/**
 * Lazy-loaded inline timeline of audit-log entries by the target user during
 * the 2-hour window after the impersonation started.
 */
function SessionReplay({ auditId, actionsCount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getImpersonationTimeline(auditId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load timeline'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [auditId]);

  if (loading) {
    return (
      <div className="px-3 pb-3 pt-1 border-t border-border/20" data-testid={`session-replay-loading-${auditId}`}>
        <Loader2 className="w-3 h-3 text-muted-foreground animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-3 pb-3 pt-1 border-t border-border/20" data-testid={`session-replay-error-${auditId}`}>
        <p className="text-[10px] text-destructive">Failed to load: {error}</p>
      </div>
    );
  }
  const entries = data?.timeline || [];
  if (entries.length === 0) {
    return (
      <div className="px-3 pb-3 pt-1 border-t border-border/20" data-testid={`session-replay-empty-${auditId}`}>
        <p className="text-[10px] text-muted-foreground italic">
          No actions logged in the 2-hour window after this session started.
          {actionsCount === 0 && ' (Pure read-only support session.)'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3 pt-1 border-t border-border/20" data-testid={`session-replay-${auditId}`}>
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5 flex items-center gap-1">
        <Clock className="w-2.5 h-2.5" /> Session Timeline ({entries.length})
      </p>
      <ol className="space-y-0.5 max-h-60 overflow-auto pr-1">
        {entries.map(e => (
          <TimelineEntry key={e.audit_id} entry={e} />
        ))}
      </ol>
    </div>
  );
}

function TimelineEntry({ entry }) {
  const time = formatTimeOfDay(entry.timestamp);
  const action = humanAction(entry.action);
  const resource = entry.resource_label || entry.resource_id;
  const resourceType = entry.resource_type;
  return (
    <li className="text-[10px] text-foreground/85 flex items-start gap-2 py-0.5" data-testid={`timeline-entry-${entry.audit_id}`}>
      <span className="font-mono text-muted-foreground/80 w-12 flex-shrink-0">{time}</span>
      <span className="flex-1 min-w-0 truncate">
        <span className="text-violet-300/90">{action}</span>
        {resourceType && (
          <span className="text-muted-foreground/70"> {resourceType}</span>
        )}
        {resource && (
          <span className="text-foreground"> &quot;{resource}&quot;</span>
        )}
      </span>
    </li>
  );
}

/** "audit_log_view" → "viewed", "report_query" → "queried" — best-effort prettifier. */
function humanAction(action) {
  if (!action) return 'did something';
  const map = {
    report_query: 'queried',
    report_export_csv: 'exported CSV from',
    report_export_pdf: 'exported PDF from',
    report_create: 'created',
    report_update: 'edited',
    report_delete: 'deleted',
    report_view: 'viewed',
    model_create: 'created',
    model_update: 'edited',
    model_delete: 'deleted',
    user_login: 'logged in',
    user_logout: 'logged out',
    quickstart_generate: 'generated dashboards from',
    impersonate_user: 'started impersonation of',
    end_impersonation: 'ended impersonation',
    admin_send_password_reset: 'requested password reset for',
  };
  return map[action] || action.replace(/_/g, ' ');
}

function formatTimeOfDay(iso) {
  if (!iso) return '--:--';
  try {
    const d = new Date(iso);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  } catch { return '--:--'; }
}

/** Compact relative-time formatter ("2h ago", "3d ago"). */
function formatRelative(iso) {
  if (!iso) return 'unknown time';
  try {
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    const sec = Math.max(0, Math.floor(diffMs / 1000));
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return 'unknown time';
  }
}
